import './flow-item.css';

import { IonButton, IonIcon, IonLabel, IonReorder } from '@ionic/react';
import { addCircle, chevronDownOutline, optionsOutline } from 'ionicons/icons';
import { isNil } from 'lodash';
import { MouseEvent, ReactElement, useCallback, useMemo } from 'react';
import {
  ExecutionInfo,
  ExecutionInfoState,
} from '../../../hook/live-execution.hooks';
import {
  FlowItem,
  FlowItemSharedParameterRef,
} from '../../../model/flow.types';
import { getParameterValue } from '../../../util/flow-parameter.util';

export interface FlowItemSectionProps {
  itemIndex: number;
  item: FlowItem;
  canAddReorderItem?: boolean;
  enableCollapse?: boolean;
  collapsed?: boolean;
  label?: string;
  parentItem?: FlowItem;
  onSelectItem?: (id: string) => void;
  isItemSelected?: boolean;
  toggleCollapsed: (collapsed: boolean) => void;
  classes?: string[];
  isSectionItemExecuting?: boolean;
  executionInfo?: ExecutionInfo;
}

const FlowItemSection = (props: FlowItemSectionProps): ReactElement => {
  const {
    item,
    onSelectItem,
    collapsed,
    canAddReorderItem,
    enableCollapse,
    toggleCollapsed,
    label,
    parentItem,
    isItemSelected,
    classes,
    isSectionItemExecuting,
    executionInfo,
  } = props;

  const onItemClick = useCallback(() => {
    if (item.id && onSelectItem) {
      onSelectItem(item.id);
    }
  }, [onSelectItem, item]);

  const onCollapsedToggle = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      toggleCollapsed(!collapsed);
    },
    [collapsed, toggleCollapsed]
  );

  const color = useMemo(() => {
    return getParameterValue(item, FlowItemSharedParameterRef.SECTION_COLOR);
  }, [item]);

  const borderLeftColor = color ? `${color}` : `transparent`;

  const classList = ['flow-item-section', ...(classes ?? [])];
  const wrapperClassList = ['flow-item-section-wrapper'];

  if (item.id !== undefined) {
    wrapperClassList.push(`item-${item.id}`);
  }

  if (!isNil(parentItem)) {
    classList.push('nested');
  }

  if (
    isSectionItemExecuting &&
    collapsed &&
    executionInfo?.state === ExecutionInfoState.PLAYING
  ) {
    wrapperClassList.push('flow-item-executing-playing');
  }

  return (
    <div className={wrapperClassList.join(' ')}>
      <div
        className={classList.join(' ')}
        onClick={enableCollapse ? onCollapsedToggle : undefined}
        style={{
          borderLeftColor,
          backgroundColor: isItemSelected
            ? 'rgba(var(--ion-color-primary-rgb), 0.2)'
            : undefined,
        }}
      >
        <div className="flow-item-section-title">
          <IonLabel>{label ?? item.name}</IonLabel>
        </div>
        <div className="flow-item-section-control-icons">
          {enableCollapse && (
            <>
              <IonButton
                fill="clear"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onItemClick();
                }}
              >
                <IonIcon icon={optionsOutline} />
              </IonButton>
              <IonButton
                className={collapsed ? 'section-collapsed' : 'section-expanded'}
                fill="clear"
                onClick={onCollapsedToggle}
              >
                <IonIcon icon={chevronDownOutline} />
              </IonButton>
            </>
          )}
          {canAddReorderItem && (
            <IonButton color={'clear'} className="ion-no-padding">
              <IonReorder />
            </IonButton>
          )}
        </div>
      </div>
    </div>
  );
};

interface FlowItemSectionSecondaryProps {
  canAddReorderItem?: boolean;
  isSelected?: boolean;
  onAddItemsAfter?: () => void;
}

const FlowItemPrecedingStem = (
  props: FlowItemSectionSecondaryProps
): ReactElement => {
  const { canAddReorderItem, isSelected, onAddItemsAfter } = props;

  const secondaryClassList = ['flow-item-preceding-container'];
  if (canAddReorderItem) {
    secondaryClassList.push('add-action-visible');
  }

  if (isSelected) {
    secondaryClassList.push('flow-item-selected');
  }

  return (
    <div className={secondaryClassList.join(' ')}>
      <div
        className="machine-flow-item-stem-container"
        onClick={(e) => {
          if (canAddReorderItem && onAddItemsAfter) {
            e.stopPropagation();
            e.preventDefault();
            onAddItemsAfter();
          }
        }}
      >
        <div className="machine-flow-item-stem" />
        <div className={'machine-flow-item-add'}>
          <IonButton
            data-cy="flow-item-add"
            tabIndex={-1}
            style={{ outline: 'none' }}
            fill="clear"
          >
            <IonIcon icon={addCircle} color="success" />
          </IonButton>
        </div>
        <div className="machine-flow-item-stem" />
      </div>
    </div>
  );
};

export { FlowItemSection, FlowItemPrecedingStem };
